/**
 * MFA grace-window signal — the reader for a header the backend has been sending
 * all along.
 *
 * 🔴 WHY THIS FILE EXISTS. `mfaEnforcement.js:171-206` sets four response headers on
 * EVERY request made by a user inside the grace window — an administrator on any
 * tenant, and ALSO an ordinary user on a tenant with mfa_required = TRUE, which is
 * seven of the ten live tenants (mfaEnforcesFrom:82-84):
 *
 *     X-MFA-Enrolment-Required: true
 *     X-MFA-Grace-Days-Remaining: <n>          (a CEIL — cannot rebuild the date)
 *     X-MFA-Enforces-At: <ISO-8601 instant>    (added 25 Aug; the date comes from here)
 *     X-MFA-Enforcement-Basis: tenant_policy | admin_role
 *
 * PROBED 2026-08-24: nothing in the frontend read either of them. The countdown has
 * existed since the control shipped and has never been shown to anyone. That is the
 * house rule almost verbatim — a signal with no reader is not a control — and it is
 * why the 21 August lockout arrived with no warning: the warning was being emitted
 * on every response and thrown away.
 *
 * The approved design (Administrator MFA Control Rationale v1.0, §3) requires a
 * prompt "at each sign-in that cannot be dismissed without explicit acknowledgement",
 * stating the enforcement date and the days remaining. This module carries the
 * numbers from the header to that prompt.
 *
 * ONCE PER SIGN-IN (decision 5, 4 Sep 2026; PR-E). The signal itself stays
 * in memory, but the ANSWER is held in sessionStorage keyed by user id:
 *
 *   - a reload, a deep link, or a tab restore inside the same sign-in does NOT
 *     re-prompt (the 3 Sep click-walk saw the prompt on every full page load,
 *     which is what "at each sign-in" degraded into once the signal lived only
 *     in module memory);
 *   - a new sign-in DOES re-prompt: resetMfaGraceAnswered() runs on successful
 *     login, and clearTokens() (logout, idle timeout, refresh failure) calls
 *     clearMfaGrace() — the WHOLE window, not just the answer, because
 *     `required` is sticky and would otherwise render the prompt on /login;
 *   - a new tab re-prompts (sessionStorage is per tab) — accepted;
 *   - a different user in the same tab re-prompts (the key carries the user id).
 *
 * localStorage is still refused: it would survive logout and turn the control
 * into a one-time notice. Rationale doc §3 reads "at each sign-in" — this is
 * that, not "at each page load"; the one-line amendment is owed to the doc.
 */

let state = {
  required: false,
  daysRemaining: null,
  // The enforcement instant, ISO-8601, straight from X-MFA-Enforces-At.
  //
  // 🔴 DO NOT DERIVE THIS FROM daysRemaining. That was the original bug: the day
  // count is a CEIL of a fractional day, so `now + daysRemaining` overshoots
  // whenever the remainder is non-zero. On 25 Aug 2026 five of seven production
  // tenants enforced on 1 October at a time of day past ~10:00, returned 38, and
  // displayed 2 October — a day after the account is actually blocked.
  enforcesAt: null,
  // 'tenant_policy' | 'admin_role' | null — which rule flagged this user.
  basis: null,
  // Answered THIS SIGN-IN. Hydrated from sessionStorage by hydrateMfaGraceAnswered(userId).
  answered: false,
};

const ANSWERED_KEY = 'igrc.mfaGrace.answeredBy';
const readAnsweredBy = () => { try { return sessionStorage.getItem(ANSWERED_KEY); } catch { return null; } };
const writeAnsweredBy = (userId) => { try { sessionStorage.setItem(ANSWERED_KEY, String(userId)); } catch { /* storage unavailable: falls back to in-memory */ } };
const removeAnsweredBy = () => { try { sessionStorage.removeItem(ANSWERED_KEY); } catch { /* ignore */ } };

const subscribers = new Set();

const notify = () => {
  for (const fn of subscribers) {
    try { fn(state); } catch { /* a broken subscriber must not break the signal */ }
  }
};

/**
 * Subscribe to the signal. Calls back IMMEDIATELY with current state, so a
 * component mounting after the headers arrived still sees them — there is
 * deliberately no separate read() accessor, because one would be a second way to
 * ask the same question and the two could drift.
 */
export const subscribeMfaGrace = (fn) => {
  subscribers.add(fn);
  fn(state);
  return () => subscribers.delete(fn);
};

/**
 * Update from an axios response's headers. Axios lower-cases response header names,
 * so these are read lower-case; reading 'X-MFA-...' would silently never match.
 *
 * Absence is NOT treated as "no longer required". The headers are only set on
 * responses that pass through enforceMfaPolicy, and plenty do not — a bypassed
 * /auth/mfa/* call, a static asset, a 304. Clearing on every header-less response
 * would make the prompt flicker away on the first such call. Only an explicit
 * enrolment (which the caller signals via clearMfaGrace) ends the window.
 */
export const updateMfaGraceFromHeaders = (headers) => {
  if (!headers) return;
  const required = String(headers['x-mfa-enrolment-required'] ?? '') === 'true';
  if (!required) return;

  const raw = Number(headers['x-mfa-grace-days-remaining']);
  const days = Number.isFinite(raw) ? raw : null;

  // Validated, not trusted: an unparseable value must read as "no date" so the
  // prompt drops the date line, rather than rendering "Invalid Date" at someone.
  const rawAt = headers['x-mfa-enforces-at'];
  const parsedAt = rawAt ? new Date(rawAt) : null;
  const enforcesAt = parsedAt && !Number.isNaN(parsedAt.getTime()) ? rawAt : null;

  // Only the two values the backend actually sends are accepted; anything else
  // reads as null and the copy falls back to wording true of both.
  const rawBasis = headers['x-mfa-enforcement-basis'];
  const basis = rawBasis === 'tenant_policy' || rawBasis === 'admin_role' ? rawBasis : null;

  if (state.required === true
    && state.daysRemaining === days
    && state.enforcesAt === enforcesAt
    && state.basis === basis) return;
  state = { ...state, required: true, daysRemaining: days, enforcesAt, basis };
  notify();
};

/** The user answered the prompt — hide it for the rest of THIS sign-in (this tab). */
export const markMfaGraceAnswered = (userId) => {
  if (userId != null) writeAnsweredBy(userId);
  if (state.answered) return;
  state = { ...state, answered: true };
  notify();
};

/**
 * On mount / when the signed-in user is known: if this user already answered
 * during this sign-in (same tab), keep the prompt hidden across reloads. A
 * different user id in the key means someone else answered — re-prompt.
 */
export const hydrateMfaGraceAnswered = (userId) => {
  if (userId == null) return;
  const answered = readAnsweredBy() === String(userId);
  if (state.answered === answered) return;
  state = { ...state, answered };
  notify();
};

/** New sign-in or sign-out: the next sign-in must be prompted again. */
export const resetMfaGraceAnswered = () => {
  removeAnsweredBy();
  if (!state.answered) return;
  state = { ...state, answered: false };
  notify();
};

/** Enrolment completed — the window is genuinely over. */
export const clearMfaGrace = () => {
  removeAnsweredBy();
  state = { required: false, daysRemaining: null, enforcesAt: null, basis: null, answered: false };
  notify();
};
